import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppWidget from '../../components/feature/WhatsAppWidget';
import CookieConsent from '../../components/feature/CookieConsent';
import { updateSEO, getBaseUrl } from '../../utils/seo';

const HomePage = () => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    updateSEO({
      title: 'Bursa Sulama Sistemleri | Nilüfer Görükle Otomatik Sulama',
      description: 'Bursa\'da 12 yıllık deneyimle profesyonel sulama sistemleri kurulumu. Nilüfer, Görükle ve çevre bölgelerde yağmurlama, damlama, otomatik sulama sistemleri. 500+ mutlu müşteri, 7/24 hizmet.',
      keywords: 'bursa sulama sistemleri, nilüfer otomatik sulama, görükle damlama sulama, yağmurlama sistemleri bursa, otomatik sulama kurulumu, bahçe sulama sistemleri',
      canonical: `${baseUrl}/`,
      ogImage: 'https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/447d769c117baf03cd3b358fbf217ae4.png',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Bursa Sulama Sistemleri',
        image: 'https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/447d769c117baf03cd3b358fbf217ae4.png',
        description: 'Bursa\'da profesyonel sulama sistemleri kurulumu ve bakım hizmetleri',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Atatürk Caddesi No:72, Hasan Boztürk Apartmanı',
          addressLocality: 'Görükle, Nilüfer',
          addressRegion: 'Bursa',
          addressCountry: 'TR'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '40.223456',
          longitude: '28.876543'
        },
        url: baseUrl,
        telephone: '+905071344668',
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '18:00'
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '17:00'
          }
        ],
        priceRange: '$$',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '500'
        }
      }
    });
  }, []);

  const [counts, setCounts] = useState({
    years: 0,
    customers: 0,
    team: 0,
  });

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      years: 12,
      customers: 500,
      team: 8,
    };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounts({
        years: Math.floor(targets.years * progress),
        customers: Math.floor(targets.customers * progress),
        team: Math.floor(targets.team * progress),
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      title: 'Sprey Sprink Sistemleri',
      description:
        'Hassas sulama gerektiren alanlar için ideal sprey sistemleri.',
      icon: 'ri-contrast-drop-line',
      image:
        'https://public.readdy.ai/ai/img_res/01ad654381d4728aab8634d9f37af73e.jpg',
    },
    {
      title: 'Yağmurlama Sistemleri',
      description:
        'Geniş alanlarda etkili ve homojen sulama çözümleri sunuyoruz.',
      icon: 'ri-water-flash-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20lawn%20sprinkler%20irrigation%20system%20spraying%20water%20on%20green%20grass%20in%20a%20garden%2C%20modern%20automatic%20watering%20equipment%20with%20water%20droplets%20visible%2C%20clean%20simple%20light%20background%20highlighting%20the%20sprinkler%20system%2C%20high%20quality%20commercial%20photography&width=420&height=320&seq=service-1&orientation=landscape',
    },
    {
      title: 'Damlama Sistemleri',
      description:
        'Su tasarrufu sağlayan, verimli damlama sulama çözümleri.',
      icon: 'ri-drop-line',
      image:
        'https://readdy.ai/api/search-image?query=drip%20irrigation%20system%20with%20water%20droplets%20on%20black%20tubing%20near%20plant%20roots%20in%20soil%2C%20efficient%20water%20saving%20agriculture%20technology%2C%20close%20up%20view%2C%20clean%20simple%20light%20background%2C%20professional%20agricultural%20photography&width=420&height=320&seq=service-3&orientation=landscape',
    },
    {
      title: 'Tamir ve Tadilat Hizmetleri',
      description:
        'Mevcut sulama sistemlerinizin bakım, onarım ve iyileştirme hizmetleri.',
      icon: 'ri-tools-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20technician%20repairing%20irrigation%20system%20equipment%20with%20tools%2C%20maintenance%20work%20on%20sprinkler%20system%20components%2C%20hands%20working%20on%20water%20pipes%20and%20valves%2C%20clean%20simple%20light%20background%2C%20professional%20service%20photography&width=420&height=320&seq=service-5&orientation=landscape',
    },
    {
      title: 'Depo ve Hidrofor Kurulumları',
      description:
        'Profesyonel su depolama ve basınçlandırma sistemleri kurulumu.',
      icon: 'ri-database-2-line',
      image:
        'https://readdy.ai/api/search-image?query=modern%20water%20storage%20tank%20and%20hydrophore%20pump%20system%20installation%2C%20professional%20plumbing%20equipment%20with%20blue%20water%20tank%20and%20pressure%20pump%2C%20clean%20industrial%20setting%20with%20simple%20light%20background%2C%20high%20quality%20technical%20photography&width=420&height=320&seq=service-4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[500px] md:min-h-[700px] lg:h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20modern%20garden%20with%20automatic%20irrigation%20sprinkler%20system%20working%20at%20sunset%2C%20water%20droplets%20sparkling%20in%20golden%20light%2C%20lush%20green%20lawn%20and%20landscaping%2C%20professional%20landscape%20photography%20with%20vibrant%20colors%20and%20soft%20focus%20background%20creating%20peaceful%20atmosphere&width=1920&height=1080&seq=hero-bg-1&orientation=landscape"
            alt="Sulama Sistemleri"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#14B8A6]/40 via-[#0A1628]/30 to-[#0A1628]/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12 w-full pt-20 pb-32 md:pb-40">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#0A1628] leading-tight mb-4 md:mb-6">
              Modern Sulama Çözümleri Bursa'nın Her Noktasında
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-white font-light mb-8 md:mb-12">
              12 Yıllık Deneyim | 500+ Mutlu Müşteri
            </p>
            <div>
              <a
                href="tel:+905071344668"
                className="inline-flex items-center gap-3 md:gap-4 bg-[#14B8A6] hover:bg-[#0F9B8E] text-white px-6 md:px-10 py-3 md:py-5 rounded-full text-base md:text-xl font-semibold transition-all duration-300 hover:scale-105 shadow-2xl whitespace-nowrap"
              >
                <span>Hemen Ara</span>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A1628] rounded-full flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-lg md:text-xl"></i>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Brand Text - KALDIRILDI */}
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#F8F6F3]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#14B8A6] rounded-full"></div>
              <span className="text-xs md:text-sm text-[#0A1628] font-medium uppercase tracking-wider">
                Rakamlarla Biz
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-3 md:mb-4 px-4">
              Güvenilir Sulama Çözümleri
            </h2>
            <p className="text-base md:text-xl text-gray-600">
              Bursa'nın sulama sistemleri lideri
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {[
              { value: counts.years, label: 'Yıl Deneyim', suffix: '+' },
              { value: counts.customers, label: 'Mutlu Müşteri', suffix: '+' },
              { value: counts.team, label: 'Kişilik Ekip', suffix: '' },
              { value: 24, label: 'Saat Destek', suffix: '/7' },
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 md:p-12 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-check-double-line text-3xl md:text-5xl text-[#14B8A6]"></i>
                </div>
                <div className="text-3xl md:text-6xl font-bold text-[#0A1628] mb-2 md:mb-3">
                  {stat.value}
                  {stat.suffix}
                </div>
                <div className="text-sm md:text-lg text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#0A1628]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="mb-12 md:mb-16">
            <span className="inline-block px-4 md:px-6 py-2 border-2 border-white/20 rounded-full text-white text-xs md:text-sm font-medium mb-6 md:mb-8">
              Hizmetlerimiz
            </span>
            <h2 className="text-2xl md:text-4xl lg:text-6xl font-bold text-white leading-tight max-w-4xl">
              Profesyonel Sulama Sistemleri Kurulumu ve Bakımı
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#F5F3EF] rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-300"
              >
                <div className="relative h-48 md:h-64">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-3 md:mb-4">
                    <i
                      className={`${service.icon} text-3xl md:text-4xl text-[#14B8A6]`}
                    ></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-2 md:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <Link
              to="/hizmetler"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-3 bg-[#14B8A6] hover:bg-[#0F9B8E] text-white px-8 md:px-10 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <span>Tüm Hizmetleri Gör</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight mb-6 md:mb-8">
                12 yıllık deneyimimizle Bursa'nın her noktasında profesyonel
                sulama sistemleri kuruyoruz. Görükle, Nilüfer ve çevre
                bölgelerde güvenilir hizmet anlayışımızla yanınızdayız.
              </h2>
              <Link
                to="/hakkimizda"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-3 bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 text-[#0A1628] px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-all duration-300 whitespace-nowrap"
              >
                <span>Detaylı Bilgi</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>

              <div className="flex items-center gap-8 md:gap-16 mt-8 md:mt-16">
                <div>
                  <div className="text-4xl md:text-6xl font-bold text-[#0A1628] mb-2">
                    12+
                  </div>
                  <div className="text-sm md:text-base text-gray-600">Yıllık Deneyim</div>
                </div>
                <div>
                  <div className="text-4xl md:text-6xl font-bold text-[#0A1628] mb-2">
                    8
                  </div>
                  <div className="text-sm md:text-base text-gray-600">Kişilik Ekip</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20irrigation%20system%20installation%20team%20working%20together%20in%20a%20garden%2C%20technicians%20in%20uniform%20installing%20modern%20sprinkler%20equipment%2C%20teamwork%20and%20expertise%20visible%2C%20clean%20outdoor%20setting%20with%20simple%20light%20background%2C%20high%20quality%20professional%20service%20photography&width=600&height=700&seq=about-team-1&orientation=portrait"
                  alt="Profesyonel Ekip"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 md:top-8 left-4 md:left-8">
                  <div className="bg-white rounded-full px-4 md:px-6 py-2 md:py-3 shadow-lg">
                    <span className="text-sm md:text-base text-[#0A1628] font-semibold">
                      Görükle, Nilüfer
                    </span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A1628]/80 to-transparent p-4 md:p-8">
                  <div className="text-white">
                    <div className="text-xl md:text-2xl font-bold mb-1">
                      8 Kişilik Profesyonel Ekip
                    </div>
                    <div className="text-base md:text-lg">7/24 Hizmet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      <CookieConsent />
    </div>
  );
};

export default HomePage;
